// Function index:
//   mountQuestLog(partyId, ws) — wire the tray Quests button + overlay.
//       Fetches on open, refetches on quest.updated, pulses the tray button
//       when something meaningful changed while the panel was shut, and hands
//       a finished quest in to the NPC who set it.
//
// The Turn in button mirrors the server's `can_turn_in` and never computes
// enablement itself — the giver may be outside the loaded hex disc, or in a
// different world at the same (q,r), so the client cannot answer the question.
// The server re-validates the same predicate on POST regardless.

import { getQuests, turnInQuest, type Quest, type QuestObjective } from '../api.js';
import type { WS } from '../ws.js';

// Transitions worth interrupting the player for. Routine progress is not one.
const PULSE_REASONS = new Set(['opened', 'ready', 'closed']);

export function mountQuestLog(partyId: number, ws: WS): void {
  const btn = document.getElementById('quests-btn') as HTMLButtonElement;
  const overlay = document.getElementById('quest-overlay') as HTMLDivElement;
  const close = document.getElementById('quest-close') as HTMLButtonElement;
  const body = document.getElementById('quest-body') as HTMLDivElement;
  let open = false;

  function render(quests: Quest[]): void {
    if (quests.length === 0) {
      body.innerHTML = '<div class="quest-empty">No quests yet. The GM opens them as the party takes on goals.</div>';
      return;
    }
    body.innerHTML = quests.map(renderQuest).join('');
  }

  async function refresh(): Promise<void> {
    try {
      const { quests } = await getQuests(partyId);
      if (open) render(quests);
    } catch { /* keep previous on transient failure */ }
  }

  btn.addEventListener('click', () => {
    open = true;
    btn.classList.remove('badge-pulse');
    overlay.hidden = false;
    body.innerHTML = '<div class="quest-empty">loading…</div>';
    void refresh();
  });
  const hide = (): void => { open = false; overlay.hidden = true; };
  close.addEventListener('click', hide);
  overlay.addEventListener('click', (e) => { if (e.target === overlay) hide(); });
  document.addEventListener('keydown', (e) => { if (e.key === 'Escape' && !overlay.hidden) hide(); });

  // Delegated so the buttons survive every re-render.
  body.addEventListener('click', (e) => {
    const el = (e.target as HTMLElement).closest<HTMLButtonElement>('.quest-turnin');
    if (!el || el.disabled) return;
    el.disabled = true;
    void turnInQuest(partyId, Number(el.dataset.questId))
      .then(() => refresh())
      .catch(() => { el.disabled = false; });
  });

  ws.on('quest.updated', (msg: { reason?: string } | undefined) => {
    if (open) { void refresh(); return; }
    // Shut: don't fetch, just mark the tray so the change isn't missed.
    if (msg?.reason && PULSE_REASONS.has(msg.reason)) btn.classList.add('badge-pulse');
  });
}

function renderQuest(q: Quest): string {
  const closed = q.status !== 'active' && q.status !== 'ready';
  const objs = q.objectives.map(renderObjective).join('');
  return `<div class="quest-card${closed ? ' done' : ''}">
    <span class="badge">${esc(q.status)}</span>
    <span class="badge diff-${esc(q.difficulty ?? 'standard')}">${esc(q.difficulty ?? 'standard')}</span>
    <span class="badge">${Number(q.coin)} coin</span>
    <div class="title">${esc(q.title)}</div>
    ${q.summary ? `<div class="summary">${esc(q.summary)}</div>` : ''}
    ${objs}
    ${renderGiver(q)}
  </div>`;
}

// Counted objectives show their progress, which is the clearest signal that
// the world is verifying this itself rather than the GM judging it.
function renderObjective(o: QuestObjective): string {
  const count = o.target?.count;
  const progress = o.target?.progress;
  const tally = typeof count === 'number' && typeof progress === 'number' && !o.done
    ? ` <span class="tally">(${Number(progress)}/${Number(count)})</span>` : '';
  return `<div class="obj${o.done ? ' done' : ''}"><span class="box">${o.done ? '☑' : '☐'}</span><span>${esc(o.text)}${tally}</span></div>`;
}

function renderGiver(q: Quest): string {
  if (q.status !== 'ready' && !q.giver) return '';
  const turnIn = q.status === 'ready'
    ? `<button type="button" class="quest-turnin" data-quest-id="${Number(q.id)}"${q.can_turn_in ? '' : ' disabled'}>Turn in</button>`
    : '';

  if (!q.giver) {
    return `<div class="giver gone">No one is waiting on this — it can simply be closed out.</div>${turnIn}`;
  }
  if (!q.giver.alive) {
    return `<div class="giver gone">${esc(q.giver.name)} is gone; the debt settles itself.</div>${turnIn}`;
  }
  const where = `${esc(q.giver.name)} · (${Number(q.giver.q)}, ${Number(q.giver.r)})`;
  return q.status === 'ready'
    ? `<div class="giver">Return to ${where}</div>${turnIn}`
    : `<div class="giver">Given by ${where}</div>`;
}

// Every value interpolated into the markup above is either run through esc()
// or numerically coerced — NPC names and quest titles are model-authored, so
// they are the ones that actually matter here.
function esc(s: string): string {
  return s.replace(/[&<>"']/g, (c) => ({ '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c]!));
}
